import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, Mail, Send, CheckCircle, Clock, AlertTriangle, Users,
  BarChart3, Eye, TrendingUp, FileText, Zap, XCircle, Play, Pause,
  Activity, ThumbsUp, ThumbsDown
} from "lucide-react";
import { toast } from "sonner";
import MarketplaceMetrics from "./MarketplaceMetrics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOAN_TYPE_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR",
  cash_out: "Cash-Out Refi", construction: "Construction",
  ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};

const STATUS_CONFIG = {
  pending_review: { label: "Pending Review", bg: "bg-amber-100 text-amber-700", dot: "bg-amber-400" },
  sent: { label: "Sent", bg: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  tracking: { label: "Tracking", bg: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
};

const CONTEXT_TYPE_CONFIG = {
  missing_docs: { label: "Missing Docs", bg: "bg-red-100 text-red-700 border-red-200" },
  stale_deal: { label: "Stale Deal", bg: "bg-amber-100 text-amber-700 border-amber-200" },
  incomplete_packet: { label: "Incomplete Packet", bg: "bg-purple-100 text-purple-700 border-purple-200" },
  follow_up: { label: "Follow Up", bg: "bg-blue-100 text-blue-700 border-blue-200" },
  re_engagement: { label: "Re-engagement", bg: "bg-teal-100 text-teal-700 border-teal-200" },
  welcome: { label: "Welcome", bg: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  custom: { label: "Custom", bg: "bg-gray-100 text-gray-700 border-gray-200" },
};

export default function EmailOutreachPanel({ onViewDeal, onEntityNavigate }) {
  const [outreachData, setOutreachData] = useState(null);
  const [crmData, setCrmData] = useState(null);
  const [crmActive, setCrmActive] = useState([]);
  const [aiDrafts, setAiDrafts] = useState([]);
  const [aiCounts, setAiCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ai_drafts");
  const [sortBy, setSortBy] = useState("created_at");
  const [filterStatus, setFilterStatus] = useState("all");
  const [triggeringSeq, setTriggeringSeq] = useState(false);
  const [approvingDraft, setApprovingDraft] = useState(null);

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [outRes, crmRes, activeRes, draftsRes] = await Promise.all([
        axios.get(`${API}/outreach/dashboard/overview`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/crm/dashboard-stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/crm/active`, { headers }).catch(() => ({ data: { active_sequences: [] } })),
        axios.get(`${API}/ai-success/queue?status=all`, { headers }).catch(() => ({ data: { drafts: [], counts: {} } })),
      ]);
      setOutreachData(outRes.data);
      setCrmData(crmRes.data);
      setCrmActive(activeRes.data?.active_sequences || []);
      setAiDrafts(draftsRes.data?.drafts || []);
      setAiCounts(draftsRes.data?.counts || {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTriggerSequence = async (seqType) => {
    setTriggeringSeq(true);
    try {
      await axios.post(`${API}/crm/trigger`, { sequence_type: seqType }, { headers });
      fetchData();
    } catch (err) { console.error(err); }
    finally { setTriggeringSeq(false); }
  };

  const handleApproveDraft = async (draftId) => {
    setApprovingDraft(draftId);
    try {
      await axios.post(`${API}/ai-success/approve/${draftId}`, {}, { headers });
      fetchData();
    } catch (err) { console.error(err); toast.error("Failed to approve draft"); }
    finally { setApprovingDraft(null); }
  };

  const handleRejectDraft = async (draftId) => {
    try {
      await axios.post(`${API}/ai-success/reject/${draftId}`, {}, { headers });
      fetchData();
    } catch (err) { console.error(err); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="email-outreach-loading">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
    </div>
  );

  const crm = crmData || {};
  const out = outreachData || {};
  const outKpis = out.kpis || {};
  const funnel = out.funnel || [];
  const deals = out.deals || [];

  const SEQ_LABELS = {
    borrower_incomplete: "Incomplete App Follow-Up",
    borrower_docs_reminder: "Document Reminder",
    lender_new_deal: "New Deal Alert",
    stale_deal_reactivation: "Stale Deal Reactivation",
    borrower_deal_funded: "Post-Funding Follow-Up",
  };

  const filtered = deals.filter(d => filterStatus === "all" || d.outreach_status === filterStatus);
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "risk_score") return (b.risk_score || 0) - (a.risk_score || 0);
    if (sortBy === "loan_amount") return (b.loan_amount || 0) - (a.loan_amount || 0);
    if (sortBy === "sends_count") return (b.sends_count || 0) - (a.sends_count || 0);
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  return (
    <div className="space-y-5" data-testid="email-outreach-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#0A0A0A]">Email & Outreach</h2>
          <p className="text-xs text-[#888] mt-0.5">Automated sequences, deal outreach, and marketplace campaigns</p>
        </div>
      </div>

      {/* Tab Toggle */}
      <div className="flex items-center gap-1 p-1 rounded-xl bg-[#f5f0e8] border border-[#e8e0d0] w-fit" data-testid="eo-tabs">
        {[
          { id: "ai_drafts", label: `AI Drafts${(aiCounts.pending || 0) > 0 ? ` (${aiCounts.pending})` : ""}`, icon: Zap },
          { id: "outreach", label: "Deal Outreach", icon: Send },
          { id: "automation", label: "Email Automation", icon: Mail },
          { id: "marketplace", label: "Marketplace Health", icon: BarChart3 },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`eo-tab-${tab.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              activeTab === tab.id ? "bg-white text-emerald-700 shadow-sm border border-emerald-200" : "text-[#888] hover:text-[#555]"
            }`}>
            <tab.icon className="h-3.5 w-3.5" /> {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ AI DRAFTS TAB ═══ */}
      {activeTab === "ai_drafts" && (
        <div className="space-y-5" data-testid="eo-ai-drafts">
          {/* Draft Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Pending Review", value: aiCounts.pending || 0, color: "text-orange-600", bg: "bg-orange-50 border-orange-100" },
              { label: "Approved", value: aiCounts.approved || 0, color: "text-blue-600", bg: "bg-blue-50 border-blue-100" },
              { label: "Sent", value: aiCounts.sent || 0, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-100" },
              { label: "Rejected", value: aiCounts.rejected || 0, color: "text-red-500", bg: "bg-red-50 border-red-100" },
            ].map((stat, i) => (
              <div key={stat.label} className={`rounded-xl border ${stat.bg} p-3.5`}>
                <div className="text-[9px] uppercase tracking-wider text-[#999] font-semibold mb-1">{stat.label}</div>
                <div className={`text-xl font-bold ${stat.color}`}>{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Scan Button */}
          <div className="flex items-center gap-3">
            <button onClick={async () => {
              try {
                const res = await axios.post(`${API}/ai-success/scan`, {}, { headers });
                toast.success(`Scan complete: ${res.data.drafts_created} new draft${res.data.drafts_created !== 1 ? "s" : ""} created`);
                fetchData();
              } catch (err) { console.error(err); toast.error("Scan failed"); }
            }}
              className="px-4 py-2.5 rounded-xl bg-purple-600 text-white text-xs font-semibold hover:bg-purple-500 transition-colors flex items-center gap-2"
              data-testid="eo-ai-scan">
              <Zap className="h-3.5 w-3.5" /> Run AI Engagement Scan
            </button>
            <span className="text-[10px] text-[#888]">Scans for missing docs, stale deals, and incomplete packets</span>
          </div>

          {/* Draft Queue */}
          <div className="space-y-3">
            {aiDrafts.filter(d => d.status === "pending").length === 0 ? (
              <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center">
                <Zap className="h-8 w-8 text-[#ccc] mx-auto mb-2" />
                <div className="text-sm text-[#888]">No pending drafts</div>
                <div className="text-[10px] text-[#bbb] mt-1">Run an AI scan or draft emails from Contact 360 views</div>
              </div>
            ) : (
              aiDrafts.filter(d => d.status === "pending").map(draft => (
                <div key={draft.draft_id} className="rounded-xl border border-orange-200 bg-white p-5 space-y-3" data-testid={`eo-draft-${draft.draft_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold border ${(CONTEXT_TYPE_CONFIG[draft.context_type] || CONTEXT_TYPE_CONFIG.custom).bg}`}>
                          {(CONTEXT_TYPE_CONFIG[draft.context_type] || { label: draft.context_type?.replace(/_/g, " ") }).label}
                        </span>
                        <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-[#faf7f2] text-[#888] border border-[#e8e0d0]">
                          {draft.user_type}
                        </span>
                      </div>
                      <div className="mt-1 text-sm font-semibold text-[#0A0A0A]">To: {draft.user_name}</div>
                      <div className="text-[10px] text-[#888]">{draft.user_email}</div>
                    </div>
                    <div className="text-[10px] text-[#999]">
                      {draft.created_by_name} &mdash; {draft.created_at ? new Date(draft.created_at).toLocaleString() : ""}
                    </div>
                  </div>

                  {/* Incomplete Packet Details */}
                  {draft.context_type === "incomplete_packet" && (draft.extra?.extra?.packet_pct != null || draft.extra?.packet_pct != null) && (
                    <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/50 p-3" data-testid={`eo-draft-packet-info-${draft.draft_id}`}>
                      <div className="shrink-0">
                        <div className="text-lg font-bold text-amber-700">{draft.extra?.extra?.packet_pct ?? draft.extra?.packet_pct}%</div>
                        <div className="text-[9px] text-amber-600 uppercase tracking-wider font-semibold">Complete</div>
                      </div>
                      <div className="h-8 w-px bg-amber-200" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-semibold text-amber-700 mb-1">Missing Sections</div>
                        <div className="flex flex-wrap gap-1">
                          {(draft.extra?.extra?.missing || draft.extra?.missing || []).map((section, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-100 text-amber-800 border border-amber-200">{section}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Email Preview */}
                  <div className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-4">
                    <div className="text-xs font-bold text-[#333] mb-2">Subject: {draft.subject}</div>
                    <div className="text-xs text-[#555] leading-relaxed" dangerouslySetInnerHTML={{ __html: draft.body_html }} />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <button onClick={() => handleApproveDraft(draft.draft_id)}
                      disabled={approvingDraft === draft.draft_id}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50"
                      data-testid={`eo-approve-${draft.draft_id}`}>
                      {approvingDraft === draft.draft_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsUp className="h-3 w-3" />}
                      Approve & Send
                    </button>
                    <button onClick={() => handleRejectDraft(draft.draft_id)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 transition-colors"
                      data-testid={`eo-reject-${draft.draft_id}`}>
                      <ThumbsDown className="h-3 w-3" /> Reject
                    </button>
                  </div>
                </div>
              ))
            )}

            {/* Sent/Rejected History */}
            {aiDrafts.filter(d => d.status === "sent" || d.status === "rejected").length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-[#999] mb-3">History</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {aiDrafts.filter(d => d.status !== "pending").map(draft => (
                    <div key={draft.draft_id} className={`rounded-xl border p-3 flex items-center justify-between ${
                      draft.status === "sent" ? "border-emerald-200 bg-emerald-50/30" : "border-red-200 bg-red-50/30"
                    }`} data-testid={`eo-history-${draft.draft_id}`}>
                      <div>
                        <div className="text-xs font-medium text-[#333]">{draft.user_name} — {draft.subject}</div>
                        <div className="text-[10px] text-[#888]">{draft.context_type?.replace(/_/g, " ")} &mdash; {draft.sent_at ? new Date(draft.sent_at).toLocaleString() : draft.reviewed_at ? new Date(draft.reviewed_at).toLocaleString() : ""}</div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                        draft.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"
                      }`}>{draft.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ DEAL OUTREACH TAB ═══ */}
      {activeTab === "outreach" && (
        <div className="space-y-5">
          {/* Outreach KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: "Total Outreach", value: outKpis.total_outreach || 0, icon: Zap, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-100" },
              { label: "Pending Review", value: outKpis.pending_review || 0, icon: Eye, color: "text-amber-600", bg: "bg-amber-50 border-amber-100" },
              { label: "Sent to Lenders", value: outKpis.sent_to_lenders || 0, icon: Send, color: "text-blue-600", bg: "bg-blue-50 border-blue-100" },
              { label: "Response Rate", value: `${outKpis.response_rate || 0}%`, icon: TrendingUp, color: "text-purple-600", bg: "bg-purple-50 border-purple-100" },
              { label: "Term Sheets", value: outKpis.term_sheets || 0, icon: FileText, color: "text-[#b89d5a]", bg: "bg-[#faf7f2] border-[#e8e0d0]" },
              { label: "Avg Response", value: (outKpis.avg_response_hours || 0) > 0 ? `${outKpis.avg_response_hours}h` : "N/A", icon: Clock, color: "text-cyan-600", bg: "bg-cyan-50 border-cyan-100" },
            ].map((kpi, i) => (
              <motion.div key={kpi.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className={`rounded-xl border ${kpi.bg} p-3.5`} data-testid={`eo-kpi-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <kpi.icon className={`h-3.5 w-3.5 ${kpi.color}`} />
                  <span className="text-[9px] uppercase tracking-wider text-[#999] font-semibold">{kpi.label}</span>
                </div>
                <div className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</div>
              </motion.div>
            ))}
          </div>

          {/* Funnel + Response Breakdown */}
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Outreach Funnel */}
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-5" data-testid="eo-funnel">
              <h4 className="text-xs font-bold text-[#444] uppercase tracking-wider mb-4 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-[#b89d5a]" /> Outreach Funnel
              </h4>
              <div className="space-y-2.5">
                {funnel.map((step, i) => {
                  const maxCount = funnel[0]?.count || 1;
                  const pct = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                  const colors = ["bg-emerald-400", "bg-amber-400", "bg-blue-400", "bg-cyan-400", "bg-purple-400", "bg-[#b89d5a]"];
                  return (
                    <div key={step.stage} className="flex items-center gap-3">
                      <span className="text-[10px] font-semibold text-[#444] w-28 truncate">{step.label}</span>
                      <div className="flex-1 h-7 rounded-full bg-[#faf7f2] border border-[#e8e0d0] overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(pct, 2)}%` }}
                          transition={{ delay: i * 0.1, duration: 0.6 }}
                          className={`h-full rounded-full ${colors[i % colors.length]} flex items-center justify-end pr-2`}
                          style={{ opacity: 0.8 }}>
                          {pct > 15 && <span className="text-[9px] text-white font-bold">{step.count}</span>}
                        </motion.div>
                      </div>
                      {pct <= 15 && <span className="text-xs font-bold text-[#444] w-6 text-right">{step.count}</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Response Breakdown */}
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-5" data-testid="eo-response-breakdown">
              <h4 className="text-xs font-bold text-[#444] uppercase tracking-wider mb-4 flex items-center gap-2">
                <Mail className="h-4 w-4 text-[#b89d5a]" /> Lender Response Breakdown
              </h4>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { label: "Emails Sent", value: outKpis.total_sends || 0, icon: Send, color: "text-blue-600" },
                  { label: "Responses", value: outKpis.total_responses || 0, icon: CheckCircle, color: "text-emerald-600" },
                  { label: "Interested", value: outKpis.interested || 0, icon: TrendingUp, color: "text-emerald-600" },
                  { label: "Passed", value: outKpis.passed || 0, icon: XCircle, color: "text-red-500" },
                ].map(m => (
                  <div key={m.label} className="rounded-xl bg-[#faf7f2] border border-[#e8e0d0] p-3">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <m.icon className={`h-3.5 w-3.5 ${m.color}`} />
                      <span className="text-[9px] text-[#999] uppercase tracking-wider">{m.label}</span>
                    </div>
                    <div className={`text-xl font-bold ${m.color}`}>{m.value}</div>
                  </div>
                ))}
              </div>
              {/* Velocity */}
              <div className="space-y-2">
                <div className="text-[10px] text-[#888] uppercase tracking-wider font-semibold">Pipeline Velocity</div>
                <div className="flex items-center gap-3 rounded-lg bg-[#faf7f2] border border-[#e8e0d0] p-3">
                  <Zap className="h-4 w-4 text-amber-500" />
                  <div>
                    <div className="text-sm font-semibold text-[#333]">Avg Pipeline to Send</div>
                    <div className="text-xs text-[#888]">{(outKpis.avg_pipeline_hours || 0) > 0 ? `${outKpis.avg_pipeline_hours} hours` : "No data yet"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Active Outreach Table */}
          <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="eo-deals-table">
            <div className="px-5 py-4 border-b border-[#e8e0d0] flex items-center justify-between flex-wrap gap-3">
              <h4 className="text-sm font-bold text-[#0A0A0A] flex items-center gap-2">
                <Users className="h-4 w-4 text-[#b89d5a]" /> Active Outreach ({filtered.length} deals)
              </h4>
              <div className="flex gap-2 items-center">
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-[#e8e0d0] bg-[#faf7f2] text-[#444] focus:outline-none"
                  data-testid="eo-filter-status">
                  <option value="all">All Statuses</option>
                  <option value="pending_review">Pending Review</option>
                  <option value="sent">Sent</option>
                </select>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-[#e8e0d0] bg-[#faf7f2] text-[#444] focus:outline-none"
                  data-testid="eo-sort-by">
                  <option value="created_at">Most Recent</option>
                  <option value="risk_score">Risk Score</option>
                  <option value="loan_amount">Loan Amount</option>
                  <option value="sends_count">Most Sends</option>
                </select>
              </div>
            </div>
            {sorted.length === 0 ? (
              <div className="text-center py-12 text-sm text-[#888]">No outreach records found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#faf7f2] text-[10px] uppercase tracking-wider text-[#888]">
                      <th className="text-left px-4 py-3 font-semibold">Deal</th>
                      <th className="text-left px-4 py-3 font-semibold">Type</th>
                      <th className="text-right px-4 py-3 font-semibold">Amount</th>
                      <th className="text-center px-4 py-3 font-semibold">Risk</th>
                      <th className="text-center px-4 py-3 font-semibold">Status</th>
                      <th className="text-center px-4 py-3 font-semibold">Lenders</th>
                      <th className="text-center px-4 py-3 font-semibold">Responses</th>
                      <th className="text-right px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f0ebe0]">
                    {sorted.map(deal => {
                      const cfg = STATUS_CONFIG[deal.outreach_status] || STATUS_CONFIG.pending_review;
                      const statuses = deal.send_statuses || {};
                      const intCount = statuses.interested || 0;
                      const tsCount = statuses.term_sheet || 0;
                      const passCount = statuses.passed || 0;
                      return (
                        <tr key={deal.lead_id} className="hover:bg-[#faf7f2]/50 transition-colors cursor-pointer"
                          onClick={() => onEntityNavigate?.("deal", deal.lead_id)} data-testid={`eo-deal-${deal.lead_id}`}>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-[#0A0A0A] text-sm">{deal.borrower_name}</div>
                            <div className="text-[10px] text-emerald-600 font-bold">{deal.deal_number}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-[#faf7f2] border border-[#e8e0d0] text-[#555]">
                              {deal.loan_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-[#0A0A0A]">
                            ${(deal.loan_amount || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm font-bold ${
                              (deal.risk_score || 0) >= 75 ? "text-emerald-600" : (deal.risk_score || 0) >= 50 ? "text-amber-600" : "text-orange-600"
                            }`}>{deal.risk_score || "—"}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold ${cfg.bg}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
                              {cfg.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-sm font-semibold text-[#333]">{deal.qualified_lenders}</span>
                            {deal.sends_count > 0 && <div className="text-[10px] text-blue-600">{deal.sends_count} sent</div>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {deal.sends_count > 0 ? (
                              <div className="flex items-center justify-center gap-1">
                                {intCount > 0 && <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-emerald-100 text-emerald-700">{intCount} int</span>}
                                {tsCount > 0 && <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-purple-100 text-purple-700">{tsCount} TS</span>}
                                {passCount > 0 && <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-red-100 text-red-600">{passCount} pass</span>}
                                {intCount + tsCount + passCount === 0 && <span className="text-[10px] text-[#999]">Awaiting</span>}
                              </div>
                            ) : <span className="text-[10px] text-[#ccc]">—</span>}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={(e) => { e.stopPropagation(); onViewDeal?.(deal.lead_id); }}
                              className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-[10px] font-semibold hover:bg-emerald-600 transition-colors inline-flex items-center gap-1"
                              data-testid={`eo-view-${deal.lead_id}`}>
                              <Eye className="h-3 w-3" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ EMAIL AUTOMATION TAB ═══ */}
      {activeTab === "automation" && (
        <div className="space-y-5">
          {/* Automation KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 text-center">
              <div className="text-2xl font-bold text-[#0A0A0A]">{crm.total_active_sequences || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#888]">Active Sequences</div>
            </div>
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">{crm.total_emails_sent || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#888]">Emails Sent</div>
            </div>
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 text-center">
              <div className="text-2xl font-bold text-emerald-600">{crm.lender_engagement_rate || 0}%</div>
              <div className="text-[10px] uppercase tracking-wider text-[#888]">Engagement Rate</div>
            </div>
          </div>

          {/* Email Sequences */}
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#999] mb-4 flex items-center gap-2">
              <Mail className="h-4 w-4 text-purple-500" /> Email Sequences
            </h3>
            <div className="space-y-3">
              {Object.entries(crm.sequences_by_type || {}).map(([type, stats]) => (
                <div key={type} className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-[#0A0A0A]">{SEQ_LABELS[type] || type}</div>
                    <div className="text-xs text-[#888]">{stats.active} active / {stats.completed} completed</div>
                  </div>
                  <button onClick={() => handleTriggerSequence(type)} disabled={triggeringSeq}
                    data-testid={`eo-trigger-${type}`}
                    className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50">
                    {triggeringSeq ? "..." : "Trigger"}
                  </button>
                </div>
              ))}
              {Object.keys(crm.sequences_by_type || {}).length === 0 && (
                <div className="text-center py-6 text-xs text-[#999]">No sequences configured</div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#999] mb-4 flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" /> Quick Actions
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <button onClick={() => handleTriggerSequence("borrower_incomplete")} disabled={triggeringSeq}
                data-testid="eo-qa-incomplete"
                className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left hover:border-amber-300 transition-colors">
                <FileText className="h-5 w-5 text-amber-600 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-[#0A0A0A]">Follow Up Incomplete Apps</div>
                  <div className="text-xs text-[#888]">{crm.incomplete_applications || 0} pending</div>
                </div>
              </button>
              <button onClick={() => handleTriggerSequence("borrower_docs_reminder")} disabled={triggeringSeq}
                data-testid="eo-qa-docs"
                className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-left hover:border-blue-300 transition-colors">
                <FileText className="h-5 w-5 text-blue-600 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-[#0A0A0A]">Send Doc Reminders</div>
                  <div className="text-xs text-[#888]">{crm.needs_docs || 0} need docs</div>
                </div>
              </button>
              <button onClick={() => handleTriggerSequence("stale_deal_reactivation")} disabled={triggeringSeq}
                data-testid="eo-qa-stale"
                className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-left hover:border-red-300 transition-colors">
                <Clock className="h-5 w-5 text-red-500 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-[#0A0A0A]">Reactivate Stale Deals</div>
                  <div className="text-xs text-[#888]">{crm.stale_deals || 0} stale</div>
                </div>
              </button>
            </div>
          </div>

          {/* Active Sequences List */}
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#999] mb-4">Active Sequences ({crmActive.length})</h3>
            {crmActive.length === 0 ? (
              <div className="text-center py-6 text-xs text-[#999]">No active sequences</div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {crmActive.map(seq => (
                  <div key={seq.sequence_id} className="rounded-lg border border-[#e8e0d0] bg-[#faf7f2] p-3 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-medium text-[#0A0A0A]">{seq.sequence_name}</div>
                      <div className="text-[10px] text-[#888]">{seq.target_email} &mdash; Step {seq.current_step + 1}/{seq.total_steps}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      seq.status === "active" ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"
                    }`}>{seq.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ MARKETPLACE HEALTH TAB ═══ */}
      {activeTab === "marketplace" && (
        <MarketplaceMetrics />
      )}
    </div>
  );
}
